import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const Timer = ({ onSessionCreated }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning && startTime) {
      // Calculate elapsed time based on actual timestamps
      // This works even when the app is backgrounded
      intervalRef.current = setInterval(() => {
        const now = new Date();
        const start = new Date(startTime);
        const elapsedSeconds = Math.floor((now - start) / 1000);
        setSeconds(elapsedSeconds);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, startTime]);

  // Also recalculate when app comes back to foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isRunning && startTime) {
        // App came back to foreground, recalculate time
        const now = new Date();
        const start = new Date(startTime);
        const elapsedSeconds = Math.floor((now - start) / 1000);
        setSeconds(elapsedSeconds);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRunning, startTime]);

  // Persist timer state to localStorage
  useEffect(() => {
    if (isRunning && startTime) {
      localStorage.setItem('timerState', JSON.stringify({
        isRunning: true,
        startTime: startTime
      }));
    } else {
      localStorage.removeItem('timerState');
    }
  }, [isRunning, startTime]);

  // Restore timer state on mount
  useEffect(() => {
    const savedState = localStorage.getItem('timerState');
    if (savedState) {
      try {
        const { isRunning: wasRunning, startTime: savedStartTime } = JSON.parse(savedState);
        if (wasRunning && savedStartTime) {
          setStartTime(savedStartTime);
          setIsRunning(true);
          // Calculate elapsed time
          const now = new Date();
          const start = new Date(savedStartTime);
          const elapsedSeconds = Math.floor((now - start) / 1000);
          setSeconds(elapsedSeconds);
          toast.info('Timer restored from previous session');
        }
      } catch (error) {
        localStorage.removeItem('timerState');
      }
    }
  }, []);

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleStart = () => {
    setIsRunning(true);
    setStartTime(new Date().toISOString());
    toast.success('Timer started! Enjoy your outdoor time!');
  };

  const handleStop = async () => {
    setIsRunning(false);
    const endTime = new Date().toISOString();
    const durationMinutes = Math.floor(seconds / 60);

    if (durationMinutes === 0) {
      toast.error('Session too short. Please track at least 1 minute.');
      setSeconds(0);
      setStartTime(null);
      localStorage.removeItem('timerState');
      return;
    }

    try {
      await axios.post(`${API}/sessions`, {
        start_time: startTime,
        end_time: endTime,
        duration_minutes: durationMinutes,
        session_type: 'timer'
      });
      toast.success(`Session saved! You spent ${durationMinutes} minutes outdoors!`);
      setSeconds(0);
      setStartTime(null);
      localStorage.removeItem('timerState');
      onSessionCreated();
    } catch (error) {
      toast.error('Failed to save session');
    }
  };

  const handleReset = () => {
    setIsRunning(false);
    setSeconds(0);
    setStartTime(null);
    localStorage.removeItem('timerState');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  return (
    <Card className="backdrop-blur-sm bg-white/90 border-emerald-200 shadow-xl" data-testid="timer-card">
      <CardHeader>
        <CardTitle className="text-2xl text-emerald-900">Live Timer</CardTitle>
        <CardDescription className="text-emerald-700">Track your outdoor time in real-time</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 shadow-2xl shadow-emerald-300">
          <div className="text-center">
            <div className="text-6xl font-bold text-white mb-2" data-testid="timer-display">
              {formatTime(seconds)}
            </div>
            <div className="text-emerald-100 text-lg">
              {isRunning ? 'Timer Running...' : 'Ready to start'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {!isRunning ? (
            <Button
              onClick={handleStart}
              data-testid="start-timer-btn"
              className="col-span-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-lg h-14 shadow-lg"
            >
              Start Timer
            </Button>
          ) : (
            <>
              <Button
                onClick={handleStop}
                data-testid="stop-timer-btn"
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white h-14 shadow-lg"
              >
                Stop & Save
              </Button>
              <Button
                onClick={handleReset}
                data-testid="reset-timer-btn"
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-14"
              >
                Reset
              </Button>
            </>
          )}
        </div>

        {seconds > 0 && (
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <div className="text-sm text-emerald-700">
              <strong>Current Duration:</strong> {Math.floor(seconds / 60)} minutes
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Timer;